import React, { memo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'wouter';

const STORAGE_KEY = 'dwb_sticky_cta_dismissed';

/**
 * Modern sticky CTA bar that appears after scrolling.
 * Dismissible per session.
 */
const StickyCTA = memo(function StickyCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Check session storage on mount
  useEffect(() => {
    try {
      const dismissed = sessionStorage.getItem(STORAGE_KEY);
      if (dismissed) {
        setIsDismissed(true);
      }
    } catch {
      // sessionStorage may be unavailable
    }
  }, []);

  // Track scroll position
  useEffect(() => {
    if (isDismissed) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const showThreshold = 300;
      
      // Also hide on very long pages near the footer
      const hideNearBottom = document.documentElement.scrollHeight - window.innerHeight - scrollY < 200;
      
      setIsVisible(scrollY > showThreshold && !hideNearBottom);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDismissed]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      // sessionStorage may be unavailable
    }
  };

  if (isDismissed) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-40 p-3 md:p-4"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
        >
          <div className="container max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 shadow-2xl border border-gray-700/50">
              {/* Animated gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-transparent to-blue-500/20 animate-pulse" />
              
              {/* Sparkle decorations */}
              <div className="absolute top-2 left-8 w-1 h-1 bg-primary rounded-full animate-ping" />
              <div className="absolute bottom-3 right-16 w-1 h-1 bg-blue-400 rounded-full animate-ping animation-delay-200" />
              
              {/* Content */}
              <div className="relative flex items-center justify-between gap-4 px-4 py-3 md:px-6 md:py-4">
                {/* Left side - Message */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="hidden sm:flex w-10 h-10 bg-primary/20 rounded-xl items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm md:text-base truncate">
                      Ready to transform your online presence?
                    </p>
                    <p className="text-gray-400 text-xs md:text-sm hidden sm:block">
                      Get a free consultation with our web experts
                    </p>
                  </div>
                </div>
                
                {/* Right side - CTA + Dismiss */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Link href="/enquiry">
                    <motion.span
                      className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-2.5 bg-gradient-to-r from-primary to-blue-500 hover:from-primary/90 hover:to-blue-500/90 text-white font-semibold rounded-xl text-sm md:text-base transition-all shadow-lg shadow-primary/25 cursor-pointer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span className="hidden xs:inline">Get Free Quote</span>
                      <span className="xs:hidden">Quote</span>
                      <ArrowRight className="w-4 h-4" />
                    </motion.span>
                  </Link>
                  
                  <motion.button
                    onClick={handleDismiss}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-700/50 hover:bg-gray-600/50 text-gray-400 hover:text-white transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default StickyCTA;
